import amapi from '@/api/amapi'
import { SOFTPROTO_OPTIONS, SOFTPROTO_VALUES } from '@/consts/domains'
import { APP_LANGUAGES_LIST, APP_PLATFORMS } from '@/consts/app-installer'
import { filterAppLangs, filterAppPlatforms, transformInstalledApps } from '@/helpers/app-installer'

const initialState = () => ({
  // wizard model:
  selectedApp: '',
  protocol: SOFTPROTO_VALUES.https_www.value,
  domain: '',
  language: 'en',
  path: '',
  username: '',
  password: '',
  email: '',
  submitting: false,
  availableAppsList: [], // Array<{name: string, softname: string, id: number}>
  loadingApps: false,
  loadingAppInfo: false,
  noLangsFound: false,
  loadingInstalledApps: false,
  installedAppsList: [],
  // consts:
  protocols: SOFTPROTO_OPTIONS.slice(),
  languages: APP_LANGUAGES_LIST.slice(),
})

const state = initialState

const getters = {
  selectedApp: state => state.selectedApp,
  protocol: state => state.protocol,
  domain: state => state.domain,
  language: state => state.language,
  path: state => state.path,
  username: state => state.username,
  password: state => state.password,
  email: state => state.email,
  submitting: state => state.submitting,
  availableAppsList: state => state.availableAppsList.slice(),
  loadingApps: state => state.loadingApps,
  loadingAppInfo: state => state.loadingAppInfo,
  protocols: state => state.protocols.slice(),
  languages: state => state.languages.slice(),
  noLangsFound: state => state.noLangsFound,
  loadingInstalledApps: state => state.loadingInstalledApps,
  installedAppsList: state => state.installedAppsList.slice(),
}

const mutations = {
  SET_SELECTED_APP (state, app) {
    state.selectedApp = app
  },
  SET_PROTOCOL (state, protocol) {
    state.protocol = protocol
  },
  SET_DOMAIN (state, domain) {
    state.domain = domain
  },
  SET_LANGUAGE (state, lang) {
    state.language = lang
  },
  SET_PATH (state, path) {
    state.path = path
  },
  SET_USERNAME (state, username) {
    state.username = username
  },
  SET_PASSWORD (state, password) {
    state.password = password
  },
  SET_EMAIL (state, email) {
    state.email = email
  },
  SET_SUBMITTING (state, submitting) {
    state.submitting = submitting
  },
  SET_AVAILABLE_APPS_LIST (state, apps) {
    state.availableAppsList.splice(0, state.availableAppsList.length, ...apps)
  },
  SET_LOADING_APPS (state, loading) {
    state.loadingApps = loading
  },
  SET_LOADING_APP_INFO (state, loading) {
    state.loadingAppInfo = loading
  },
  SET_LANGUAGES (state, langs) {
    state.languages.splice(0, state.languages.length, ...langs)
  },
  SET_NO_LANGS_FOUND (state, noLangs) {
    state.noLangsFound = noLangs
  },
  SET_LOADING_INSTALLED_APPS (state, loading) {
    state.loadingInstalledApps = loading
  },
  SET_INSTALLED_APPS_LIST (state, apps) {
    state.installedAppsList.splice(0, state.installedAppsList.length, ...apps)
  },
  RESET_STATE (state) {
    // eslint-disable-next-line no-undef
    const availableAppsList = structuredClone(state.availableAppsList)

    Object.assign(state, {
      ...initialState(),
      availableAppsList,
    })
  },
  RESET_FORM_STATE (state) {
    Object.assign(state, {
      ...initialState(),
      // data to keep:
      availableAppsList: state.availableAppsList,
      installedAppsList: state.installedAppsList,
      protocols: state.protocols,
      languages: state.languages,
    })
  }
}

const actions = {
  async getAppsList ({ commit, dispatch }, params) {
    commit('SET_LOADING_APPS', true)
    const data = {
      method: 'list',
      _username: params.username,
    }
    try {
      const response = await amapi.softaculous_api(data)
      // console.log(response.iscripts)
      const platformApps = filterAppPlatforms(response.iscripts, APP_PLATFORMS)
      commit('SET_AVAILABLE_APPS_LIST', platformApps)
    } catch (e) {
      dispatch('reportError', e, { root: true })
    } finally {
      commit('SET_LOADING_APPS', false)
    }
  },
  // https://www.softaculous.com/docs/api/api/#list-installed-script
  async fetchSoftInfo ({ commit, dispatch }, { username, softId }) {
    const data = {
      method: `software&soft=${softId}&giveinfo=1`,
      _username: username,
    }
    commit('SET_LOADING_APP_INFO', true)
    try {
      const response = await amapi.softaculous_api(data)
      // set supported langs:
      if (!Array.isArray(response.settings?.['Choose Language']?.language?.orig_val)) {
        commit('SET_NO_LANGS_FOUND', true)
        commit('SET_LANGUAGES', APP_LANGUAGES_LIST.slice())
      } else {
        const langs = filterAppLangs(response.settings?.['Choose Language']?.language?.orig_val)
        commit('SET_NO_LANGS_FOUND', false)
        commit('SET_LANGUAGES', langs)
      }
    } catch (e) {
      dispatch('reportError', e, { root: true })
    } finally {
      commit('SET_LOADING_APP_INFO', false)
    }
  },
  // https://www.softaculous.com/docs/api/api/#install-a-script
  async installApp ({ commit, dispatch, getters, state }, params) {
    if (!state.selectedApp?.id) {
      dispatch('reportError', 'No application selected.', { root: true })
      return
    }

    const softname = state.selectedApp.softname.toLowerCase()
    const requiresDatadir = ['ocart', 'moodle']
    const shouldProvideDatadir = requiresDatadir.some(k => softname.includes(k))

    const datadir = state.selectedApp.softname + '_' + Math.floor(Math.random() * 100000)
    commit('SET_SUBMITTING', true)
    const act = state.selectedApp.type === 'php' ? 'software' : state.selectedApp.type

    const apiParams = {
      method: `${act}&soft=${state.selectedApp.id}`,
      _username: params.username,
      // method: act + '&soft=' + state.selectedApp.id,
      payload: {
        softsubmit: 1,
        softdomain: getters.domain,
        softdirectory: getters.path || '',
        softdb: params.softdb,
        dbusername: params.dbusername,
        dbuserpass: params.dbuserpass,
        hostname: 'localhost',
        admin_username: getters.username,
        admin_pass: getters.password,
        admin_email: getters.email,
        language: getters.language,
        site_name: params.site_name || 'My Website',
        site_desc: params.site_desc || 'My Website Description',
        dbprefix: params.dbprefix || 'gg_',
        noemail: 1,
        overwrite_existing: params.overwrite_existing === true ? 1 : 0,
        softproto: state.protocol,
        eu_auto_upgrade: 1,
        auto_upgrade_plugins: 1,
        auto_upgrade_themes: 1,
        datadir: shouldProvideDatadir ? datadir : undefined,
      },
    }
    try {
      const response = await amapi.softaculous_api(apiParams)
      if (response?.error) {
        console.error('API Error Response:', response)
        throw new Error(Object.values(response.error).at(0) || 'Unexpected error occurred.')
      } else if (!response) {
        console.error('No response received from API:', response)
        throw new Error('Unexpected error occurred.')
      }
      if (response.done) {
        dispatch('reportSuccess', 'The application was installed successfully.', { root: true })
        // reset form:
        commit('RESET_STATE')
      }
      return {
        done: response.done || false,
        insid: response.insid || '',
      }
    } catch (e) {
      dispatch('reportError', e, { root: true })
      return {
        done: false,
        insid: '',
      }
    } finally {
      commit('SET_SUBMITTING', false)
    }
  },
  async fetchInstalledApps ({ commit, dispatch }, { username = '' }) {
    commit('SET_LOADING_INSTALLED_APPS', true)
    const data = {
      method: 'installations',
      _username: username || undefined,
    }
    try {
      const response = await amapi.softaculous_api(data)
      if (response.error || typeof response.installations !== 'object') {
        throw new Error(Object.values(response.error).at(0) || 'Failed to load installed applications.')
      }
      const apps = transformInstalledApps(response.installations)
      commit('SET_INSTALLED_APPS_LIST', apps)
    } catch (e) {
      dispatch('reportError', e, { root: true })
    } finally {
      commit('SET_LOADING_INSTALLED_APPS', false)
    }
  }
}

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export default module
